<template>
  <div class="container">
    <h2 class="mb-5">Collection details</h2>
    <form
      v-if="years.length || editableSectorSubmissions.length > 0"
      name="test"
      style="margin: 0 auto;"
      @submit.prevent="onSubmit"
      v-show="isReady"
      novalidate
    >
      <base-select
        v-if="editableSectorSubmissions.length === 0"
        name="years"
        v-validate="'required'"
        label="Years"
        select-label="years"
        v-model="year"
        :options="years"
        :err="errors.collect('years')"
      />
      <h2 v-else class="text-right">{{year}}</h2>
      <table class="table table-bordered s-section-association-representative-table">
        <thead>
          <tr>
            <th scope="col">Quarter</th>
            <th scope="col">1</th>
            <th scope="col">2</th>
            <th scope="col">3</th>
            <th scope="col">4</th>
            <th scope="col">Total</th>
          </tr>
        </thead>
        <tbody @click="handleTableBodyCellClick">
          <tr>
            <th scope="row">Internal pre-consumer waste recycled (T)</th>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('iq1')" @blur="handleOnBlur('iq1')" v-model="iq1" v-input-mask data-inputmask-mask="9{1,7}" >
            </td>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('iq2')" @blur="handleOnBlur('iq2')" v-model="iq2" v-input-mask data-inputmask-mask="9{1,7}" >
            </td>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('iq3')" @blur="handleOnBlur('iq3')" v-model="iq3" v-input-mask data-inputmask-mask="9{1,7}" >
            </td>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('iq4')" @blur="handleOnBlur('iq4')" v-model="iq4" v-input-mask data-inputmask-mask="9{1,7}" >
            </td>
            <td class="align-middle">
              <strong>{{iqTotal}}</strong></td>
          </tr>
          <tr v-if="isBTicked">
            <th scope="row">Volume of recyclate brought from an external source (T)</th>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('eq1')" @blur="handleOnBlur('eq1')" v-model="eq1" v-input-mask data-inputmask-mask="9{1,7}">
            </td>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('eq2')" @blur="handleOnBlur('eq2')" v-model="eq2" v-input-mask data-inputmask-mask="9{1,7}">
            </td>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('eq3')" @blur="handleOnBlur('eq3')" v-model="eq3" v-input-mask data-inputmask-mask="9{1,7}">
            </td>
            <td class="align-middle">
              <input class="border-0" @focus="handleOnFocus('eq4')" @blur="handleOnBlur('eq4')" v-model="eq4" v-input-mask data-inputmask-mask="9{1,7}">
            </td>
            <td class="align-middle">
              <strong>{{eqTotal}}</strong></td>
          </tr>
          <tr v-if="isBTicked">
            <th scope="row"></th>
            <td class="align-middle">
              {{addQ(iq1, eq1)}}
            </td>
            <td class="align-middle">
              {{addQ(iq2, eq2)}}
            </td>
            <td class="align-middle">
              {{addQ(iq3, eq3)}}
            </td>
            <td class="align-middle">
              {{addQ(iq4, eq4)}}
            </td>
            <td class="align-middle">
              <strong>{{addQ(iqTotal, eqTotal)}}</strong></td>
          </tr>
        </tbody>
      </table>
      <div class="row mt-4">
        <div class="col-6 text-left">
          <base-button
            radius="bl"
            component-class="blue"
            @click.native="$router.go(-1)"
          ><i class="fas fa-arrow-left mr-3"></i> Back
          </base-button>
        </div>
        <div
          class="col-6 text-right"
        >
          <base-button
            radius="br"
            component-class="green filled"
            type="submit"
            :disabled="!isSubmittable"
          >Save <i class="fas fa-arrow-right ml-3"></i>
          </base-button>
        </div>
      </div>
    </form>
    <div v-else>
      All years attached
      <button class="btn btn-primary ml-5" @click="$router.push({name: 'user.manage-submission-sector-association-representative'});">Go to sector submissions</button>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import BaseSelect from '@/components/inputs/select/BaseSelect';
  import BaseButton from '@/components/inputs/button/BaseButton';
  import SectorSubmissionService from '@/services/SectorSubmission/SectorSubmission';
  import getFormErrors from '@/helpers/errors';
  import { sumArray } from '@/helpers/math';
  import constances from '@/configs/constances';
  import EventBus from '@/helpers/event-bus';

  export default {
    data() {
      return {
        memoValue: '',
        year: null,
        submissionId: 0,
        company: null,
        collectionDate: '',
        iq1: 0,
        iq2: 0,
        iq3: 0,
        iq4: 0,
        eq1: 0,
        eq2: 0,
        eq3: 0,
        eq4: 0,
        composition: null,
        editableSectorSubmissions: [],
        iId: null,
        eId: null,
        formErrors: {
          company: [],
          composition: [],
        },
        isReady: false,
      };
    },
    computed: {
      ...mapState({
        sectorAssociation: state => state.user.sectorAssociation,
      }),
      sectorSubmissionsYears() {
        return Object.keys(this.$store.getters['userSectorSubmissions/groupedSectorSubmissions']);
      },
      years() {
        return ['2018', '2019', '2020'].filter(year => !this.sectorSubmissionsYears.includes(year));
      },
      isBTicked() {
        return this.sectorAssociation ? this.sectorAssociation.is_external : false;
      },
      iqTotal() {
        const { iq1, iq2, iq3, iq4 } = this;
        return sumArray([iq1, iq2, iq3, iq4]);
      },
      eqTotal() {
        const { eq1, eq2, eq3, eq4 } = this;
        return sumArray([eq1, eq2, eq3, eq4]);
      },
      filteredUserCompanyCompositions() {
        if (this.company) {
          return this.userCompanyCompositions.filter(composition => this.company.id === composition.company_id);
        }
        return [];
      },
      isSubmittable() {
        return this.iqTotal > 0 || this.eqTotal > 0;
      }
    },

    watch: {
      editableSectorSubmissions: {
        handler(newValues) {
          if (newValues.length) {
            newValues.forEach((newValue) => {
              if (newValue.source_type === 'internal') {
                this.iId = newValue.id;
                this.iq1 = newValue.q1;
                this.iq2 = newValue.q2;
                this.iq3 = newValue.q3;
                this.iq4 = newValue.q4;
              } else {
                this.eId = newValue.id;
                this.eq1 = newValue.q1;
                this.eq2 = newValue.q2;
                this.eq3 = newValue.q3;
                this.eq4 = newValue.q4;
              }
            });
          }
        },
        deep: true,
      }
    },

    methods: {
      ...mapActions({
        getUserSectorAssociation: 'user/getUserSectorAssociation',
        getUserSectorSubmissions: 'userSectorSubmissions/getUserSectorSubmissions',
      }),
      manageCompositions () {
        this.$router.push({name: 'user.composition-list'});
      },
      handleOnFocus(valueKey) {
        this.memoValue = this[valueKey];
        this[valueKey] = '';
      },
      handleOnBlur(valueKey) {
        if (this[valueKey] === '') {
          this[valueKey] = this.memoValue;
        }
        this.memoValue = '';
      },
      handleTableBodyCellClick(event) {
        const { children } = event.srcElement;
        if (children && children.length > 0) {
          children[0].focus();
        }
      },
      addQ(iq, eq) {
        return sumArray([iq, eq]);
      },
      async onSubmit () {
        const isFormValid = await this.$validator.validateAll();
        this.formErrors = await getFormErrors(this.$validator, this.formErrors);
        if (isFormValid) {
          const {
            year,
            iq1,
            iq2,
            iq3,
            iq4,
            isBTicked,
            eq1,
            eq2,
            eq3,
            eq4,
            iId,
            eId,
          } = this;

          const sector_association_values = [{
            id: iId,
            q1: iq1,
            q2: iq2,
            q3: iq3,
            q4: iq4,
            source_type: constances.sectorAssociationValue.INTERNAL,
            user_id: this.$store.getters['user/getLoggedInUserId'],
            year,
          }];

          if (isBTicked) {
            sector_association_values.push({
              id: eId,
              q1: eq1,
              q2: eq2,
              q3: eq3,
              q4: eq4,
              source_type: constances.sectorAssociationValue.EXTERNAL,
              user_id: this.$store.getters['user/getLoggedInUserId'],
              year,
            });
          }

          if (this.editableSectorSubmissions.length) {
            const updateData = {
              sector_association_values,
            };
            const { data } = await SectorSubmissionService.updateSectorSubmission(updateData);

            if (data && data.status === 0) {
              this.$snotify.error(data.message);
            } else {
              this.$router.push({name: 'user.dashboard'});
              this.$snotify.success('Submission updated.');
            }
          } else {
            const submissionData = {
              sector_association_values
            };
            this.$store.dispatch('userSectorSubmissions/save', submissionData)
              .then(() => {
                this.$router.push({name: 'user.dashboard'});
                this.$snotify.success('Submission saved.');
              }).catch(() => {
                EventBus.$emit('snotifyError', 'Sector association for this year exist');
              });
          }
        }
      },
    },

    async mounted () {
      await this.getUserSectorAssociation(this.$store.getters['user/getLoggedInUserId']);

      const year = parseInt(this.$route.params.year, 0);
      if (year) {
        const { data } = await SectorSubmissionService.getSectorSubmission(year);
        this.editableSectorSubmissions = data;
        this.year = year;
      } else {
        await this.getUserSectorSubmissions();
      }

      this.isReady = true;
    },

    components: {
      'base-select': BaseSelect,
      'base-button': BaseButton,
    }
  };

</script>

<style lang="scss">
  .s-section-association-representative-table {
    input {
      width: 100px;
      &:focus {
        outline: none;
      }
    }
  }
</style>